import React, { forwardRef, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import DatePicker from "react-datepicker";
import moment from "moment";
import FetchData, { CreateEntry, GetList, GetOne } from "../../providers/dataProvider";
import { CurrencyConvertion, FormatValueDecimal } from "../../utils/Validate";
import SaveSuccessModel from "../../components/common/SaveSuccessModel";
import ErrorMessage from "../../components/common/ErrorMessage";
import "../../pages/quotation/Forecast.scss";

const ExampleCustomInput = forwardRef(({ value, onClick, onChange }, ref) => (
  <input
    value={value}
    className="example-custom-input form-control"
    onClick={onClick}
    onChange={onChange}
    ref={ref}
    readOnly
  />
));

const toInputDate = (date) => {
  if (!date) return "";
  const dt = new Date(date);
  if (Number.isNaN(dt.getTime())) return "";

  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const toApiDateString = (date) => {
  if (!date) return "";
  const dt = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(dt.getTime())) return "";

  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}T00:00:00`;
};

const formatHistoryDate = (dateValue, withTime = false) => {
  if (!dateValue) return "-";
  const date = moment(new Date(dateValue));
  if (!date.isValid()) return "-";
  return withTime ? date.format("DD-MMM-YYYY hh:mm A") : date.format("DD-MMM-YYYY");
};

const buildHistoryTimelineRows = (histories = []) => {
  const sortedHistories = [...histories].sort(
    (a, b) => new Date(a?.revisedOn || a?.createdOn || 0) - new Date(b?.revisedOn || b?.createdOn || 0)
  );

  return sortedHistories.map((historyItem, index) => {
    const previousFromHistory =
      historyItem?.previousDate ||
      historyItem?.beforeDate ||
      historyItem?.fromDate ||
      "";
    const previousFromSequence =
      sortedHistories[index - 1]?.revisedDate ||
      sortedHistories[index - 1]?.forecastedDate ||
      sortedHistories[index - 1]?.expectedDate ||
      "";

    return {
      ...historyItem,
      revisionLabel: historyItem?.revisionLabel || `Rev ${String(index + 1).padStart(2, "0")}`,
      previousDate: previousFromHistory || previousFromSequence || "-",
      revisedDate:
        historyItem?.revisedDate ||
        historyItem?.forecastedDate ||
        historyItem?.expectedDate ||
        "-",
      revisedOn:
        historyItem?.revisedOn ||
        historyItem?.createdOn ||
        "-",
    };
  });
};

const ForecastViewModal = ({
  show,
  onHide,
  currentForecast,
  currentQuotation,
  forecastData,
  currencyDetail,
  setQuotationData,
  forecastedItemIds
}) => {
  const { data: userData } = FetchData("userprofile");
  const [openHistory, setOpenHistory] = useState({ show: false, currentItem: null });
  const [editingLine, setEditingLine] = useState(null);
  const [editValues, setEditValues] = useState({
    expectedDate: "",
  });
  const [forecastList, setForecastList] = useState([]);
  const [changeForecast, setChangeForecast] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorShow, setErrorShow] = useState({
    show: false,
    errorMessage: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [forecastApiData, setForecastApiData] = useState([]);
  const currentUserIdentityId = localStorage.getItem("authidentity");

  const loadForecastApiData = () => {
    if (!show || !forecastedItemIds?.size) {
      setForecastApiData([]);
      return;
    }

    const itemIds = Array.from(forecastedItemIds);
    const responses = [];
    let completed = 0;

    itemIds.forEach((quoteProductId) => {
      GetOne("forecast", { id: quoteProductId }, (response) => {
        completed += 1;

        if (response) {
          responses.push(response);
        }

        if (completed === itemIds.length) {
          setForecastApiData(responses);
        }
      });
    });
  };

  useEffect(() => {
    loadForecastApiData();
  }, [show, forecastedItemIds]);


const forecastedQuoteProductIds = useMemo(
  () => Array.from(forecastedItemIds || []).map(String),
  [forecastedItemIds]
);

  const forecastApiRecords = useMemo(() => {
    const ids = new Set(Array.from(forecastedItemIds || []).map(String));

    return (
      forecastApiData?.flatMap(
        (response) =>
          (response?.data || []).filter((item) =>
            ids.has(String(item?.quoteProductId))
          )
      ) || []
    );
  }, [forecastApiData, forecastedItemIds]);

  const forecastSummaryRecords = useMemo(() => {
    return (forecastData?.data || []).filter((item) =>
      forecastedQuoteProductIds.includes(String(item?.quoteProductId))
    );
  }, [forecastData?.data, forecastedQuoteProductIds]);

  const forecastRows = useMemo(() => {
    return (
      forecastApiRecords?.map((record) => {
        const summary =
          forecastSummaryRecords?.find(
            (item) =>
              String(item?.quoteProductId) === String(record?.quoteProductId)
          ) || {};
        const forecastItem = record?.productForecasts?.[0] || {};

        return {
          ...summary,
          ...record,
          forecastRecordId: record?.id,
          forecastProductId:
            forecastItem?.forecastProductId || forecastItem?.id || 0,
          rowForecast: forecastItem,
          expectedDate:
            forecastItem?.expectedDate ||
            forecastItem?.forecastedDate ||
            "",
          expectedQuantity:
            forecastItem?.expectedQuantity ??
            summary?.quantity ??
            "",
          expectedAmount:
            forecastItem?.expectedAmount ??
            summary?.forecastedAmount ??
            0,
          histories: forecastItem?.histories || [],
          revisionNo: forecastItem?.revisionNo || 0,
        };
      }) || []
    );
  }, [forecastApiRecords, forecastSummaryRecords]);

   const quotedAmount = useMemo(
    () =>
      forecastList.reduce(
        (sum, line) => sum + Number(line?.quotedAmount || 0),
        0
      ),
    [forecastList]
  );
  const forecastedAmount = useMemo(
    () =>
      forecastList.reduce(
        (sum, line) => sum + Number(line?.expectedAmount || 0),
        0
      ),
    [forecastList]
  );
  const balanceAmount = Math.max(quotedAmount - forecastedAmount, 0);

  const quotedQuantity = Number(currentForecast?.quantity || 0);
  const forecastedQuantity = useMemo(
    () =>
      forecastList.reduce(
        (sum, line) => sum + Number(line?.expectedQuantity || 0),
        0
    ),
    [forecastList]
  );

  useEffect(() => {
    if (!show) {
      setForecastList([]);
      setEditingLine(null);
      setEditValues({ expectedDate: "" });
      setOpenHistory({ show: false, currentItem: null });
      setChangeForecast(false);
      setErrorMessage("");
      setCreateMsg(null);
      setShowSuccess(false);
      setErrorShow({ show: false, errorMessage: "" });
      return;
    }

    if (createMsg || showSuccess || errorShow?.show) {
      return;
    }

    setForecastList(forecastRows);
    setEditingLine(null);
    setEditValues({ expectedDate: "" });
    setOpenHistory({ show: false, currentItem: null });
    setChangeForecast(false);
    setErrorMessage("");
  }, [show, forecastRows, createMsg, showSuccess, errorShow?.show]);

  const handleEditClick = (line) => {
    setEditingLine(line);
    setErrorMessage("");
    setEditValues({
      expectedDate: toInputDate(
        line?.expectedDate ||
        line?.forecastedDate ||
        line?.rowForecast?.expectedDate ||
        line?.productForecasts?.[0]?.expectedDate
      ),
    });
  };

  const handleEditDone = () => {
    setEditingLine(null);
    setEditValues({ expectedDate: "" });
  };

  const buildHistoryEntry = (forecastProductId, forecastedDate) => ({
    id: 0,
    forecastProductId,
    previousDate: "",
    revisedDate: toApiDateString(forecastedDate),
    revisedOn: new Date(),
    createdOn: new Date(),
    createdBy: currentUserIdentityId,
    forecastedDate: toApiDateString(forecastedDate),
  });

  const normalizeHistories = (histories = []) =>
    histories.map((historyItem) => ({
      id: historyItem?.id || 0,
      forecastProductId: historyItem?.forecastProductId || 0,
      previousDate: historyItem?.previousDate || historyItem?.beforeDate || "",
      revisedDate:
        toApiDateString(
          historyItem?.revisedDate ||
          historyItem?.forecastedDate ||
          historyItem?.expectedDate ||
          ""
        ),
      revisedOn: historyItem?.revisedOn || historyItem?.createdOn || new Date(),
      createdOn: historyItem?.createdOn || new Date(),
      createdBy: historyItem?.createdBy || currentUserIdentityId,
      forecastedDate:
        toApiDateString(
          historyItem?.forecastedDate || historyItem?.expectedDate || ""
        ),
    }));

  const handleUpdateLine = () => {
    if (!editingLine) return;

    if (!editValues?.expectedDate) {
      setErrorMessage("Please select forecast date.");
      return;
    }

    const updatedDate = new Date(editValues.expectedDate);
    const apiDate = toApiDateString(editValues.expectedDate);
    const nextList = forecastList.map((item) => {
      const isSameLine =
        String(item?.forecastRecordId || item?.id) ===
        String(editingLine?.forecastRecordId || editingLine?.id);

      if (!isSameLine) return item;

      const currentForecastItem = item?.rowForecast || item?.productForecasts?.[0] || {};
      const histories = normalizeHistories(
        currentForecastItem?.histories || item?.histories || []
      );
      const historyEntry = buildHistoryEntry(
        currentForecastItem?.forecastProductId || currentForecastItem?.id || 0,
        apiDate || currentForecastItem?.expectedDate || item?.expectedDate || item?.forecastedDate
      );
      historyEntry.previousDate = toApiDateString(
        currentForecastItem?.expectedDate ||
        item?.expectedDate ||
        item?.forecastedDate ||
        ""
      );
      histories.push(historyEntry);

      const updatedForecastItem = {
        ...currentForecastItem,
        expectedDate: apiDate || updatedDate,
        expectedQuantity:
          Number(currentForecastItem?.expectedQuantity ?? item?.expectedQuantity ?? 0),
        expectedAmount:
          Number(currentForecastItem?.expectedAmount ?? item?.expectedAmount ?? 0),
        revisionNo: (Number(currentForecastItem?.revisionNo) || 0) + 1,
        histories,
      };

      return {
        ...item,
        rowForecast: updatedForecastItem,
        expectedDate: updatedDate,
        expectedQuantity: updatedForecastItem?.expectedQuantity,
        expectedAmount: updatedForecastItem?.expectedAmount,
        histories,
        revisionNo: updatedForecastItem?.revisionNo,
        productForecasts: [
          updatedForecastItem,
          ...(item?.productForecasts?.slice(1) || []),
        ],
        isDirty: true,
      };
    });

    setForecastList(nextList);
    if (openHistory?.show && openHistory?.currentItem) {
      const updatedItem = nextList.find(
        (item) =>
          String(item?.forecastRecordId || item?.id) ===
          String(editingLine?.forecastRecordId || editingLine?.id)
      );

      if (updatedItem) {
        setOpenHistory((prev) => ({
          ...prev,
          currentItem: updatedItem,
        }));
      }
    }
    setChangeForecast(true);
    handleEditDone();
  };
  const handleSaveForecast = async () => {
    const rowsToSave = forecastList.filter((item) => item?.isDirty);

    if (rowsToSave.length === 0) {
      return;
    }

    try {
      await Promise.all(
        rowsToSave.map(
          (row) =>
            new Promise((resolve, reject) => {
              const payload = {
                id: row?.forecastRecordId || row?.id || 0,
                quotationId: row?.quotationId || currentForecast?.quotationId || currentQuotation?.id,
                quoteProductId: row?.quoteProductId || currentForecast?.quoteProductId || currentForecast?.id,
                createdAt: row?.createdAt || new Date(),
                createdBy: currentUserIdentityId,
                forecastedAmount: Number(row?.expectedAmount || 0),
                forecastedQuantity: Number(row?.expectedQuantity || 0),
                productForecasts: [
                  {
                    ...(row?.rowForecast || {}),
                    expectedDate: row?.expectedDate
                      ? toApiDateString(row.expectedDate)
                      : toApiDateString(row?.rowForecast?.expectedDate),
                    expectedQuantity: Number(row?.expectedQuantity || 0),
                    expectedAmount: Number(row?.expectedAmount || 0),
                    histories: normalizeHistories(
                      row?.histories || row?.rowForecast?.histories || []
                    ),
                    revisionNo: Number(row?.revisionNo || 0),
                  },
                ],
              };

              CreateEntry("forecast", payload, (response) => {
                if (response?.status === 200 || response?.status === 204) {
                  resolve(response);
                } else {
                  reject(response);
                }
              }, "Forecast updated successfully", "Failed to update forecast");
            })
        )
      );

      setChangeForecast(false);
      setCreateMsg({
        successData: "Successful",
        message: "Forecast updated successfully",
        status: 200,
      });
      loadForecastApiData();
    } catch (error) {
      setCreateMsg({
        successData: error?.message || error || "Failed to update forecast",
        message: "Failed to update forecast",
        status: 400,
      });
    }
  };

  const handleCloseHistory = () => {
    setOpenHistory({ show: false, currentItem: null });
  };

  useEffect(() => {
    if (!createMsg) return;

    if (String(createMsg?.message).includes("Failed")) {
      setErrorShow({
        show: true,
        errorMessage: createMsg?.successData || createMsg?.message || "Failed to update forecast",
      });
    } else if (createMsg?.status === 200 || createMsg?.status === 204) {
      setShowSuccess(true);
      if (currentQuotation?.id && setQuotationData) {
        GetList("forecast", { quoteId: currentQuotation?.id }, setQuotationData);
      }
    }
  }, [createMsg, currentQuotation?.id, onHide, setQuotationData]);

  const SuccessDoneClick = () => {
    setShowSuccess(false);
    setCreateMsg(null);
    onHide()
  };

  const ErrorDoneClick = () => {
    setErrorShow({ show: false, errorMessage: "" });
  };
  return (
    <>
      <Modal
        size="sm"
        show={show}
        onHide={onHide}
        className="forecastRightSideModalPopup drawer right-align"
      >
        <Modal.Header>
          <h5 className="modal-title">
            {currentForecast?.productGroupName} - {currentForecast?.productName}
          </h5>
          <i className="fas fa-times" onClick={onHide}></i>
        </Modal.Header>
        <Modal.Body>
          <div className="row modal-row mt-2">
            <div className="col-3 quoted-col">
              <p>Quoted ({currencyDetail?.shortCode})</p>
              <h4>
                {FormatValueDecimal(
                  CurrencyConvertion(
                    quotedAmount,
                    currentQuotation?.row?.conversionRate,
                    currencyDetail
                  )
                )}
              </h4>
            </div>
            <div className="col-3 forecasted-col">
              <p>Forecasted ({currencyDetail?.shortCode})</p>
              <h4>
                {FormatValueDecimal(
                  CurrencyConvertion(
                    forecastedAmount,
                    currentQuotation?.row?.conversionRate,
                    currencyDetail
                  )
                )}
              </h4>
            </div>
            <div className="col-3 balance-col">
              <p>Balance ({currencyDetail?.shortCode})</p>
              <h4>
                {FormatValueDecimal(
                  CurrencyConvertion(
                    balanceAmount,
                    currentQuotation?.row?.conversionRate,
                    currencyDetail
                  )
                )}
              </h4>
            </div>
          </div>

          <div className="header mt-2">
            <h6>Forecast Details</h6>
          </div>

          {editingLine && (
            <div
              className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <div className="mt-2">
                  <label>Date</label>
                  <DatePicker
                    selected={editValues?.expectedDate ? new Date(editValues.expectedDate) : null}
                    onChange={(date) =>
                      setEditValues((prev) => ({
                        ...prev,
                        expectedDate: date ? toInputDate(date) : "",
                      }))
                    }
                    customInput={<ExampleCustomInput />}
                    dayClassName={() => "example-datepicker-day-class"}
                    popperClassName="example-datepicker-class"
                    todayButton="TODAY"
                    popperPlacement="bottom-start"
                    popperProps={{ strategy: "fixed" }}
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                  />
                </div>
                <div className="d-flex justify-content-between gap-2 align-items-start">
                <div className="mt-2">
                  <label>Quantity</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingLine?.expectedQuantity ?? quotedQuantity ?? ""}
                    disabled
                  />
                </div>
                <div className="mt-2">
                  <label>Forecast Amount</label>
                  <div className="d-flex">
                  <input
                    type="text"
                    className="form-control"
                    value={
                      editingLine?.forecastedAmount !== undefined
                        ? FormatValueDecimal(
                            CurrencyConvertion(
                              editingLine?.forecastedAmount,
                              currentQuotation?.row?.conversionRate,
                              currencyDetail
                            )
                          )
                        : ""
                    }
                    disabled
                  />
                   {errorMessage && <p className="form-error mt-2 mb-0">{errorMessage}</p>}
                <button
                  type="button"
                  className="btn add-btn"
                  onClick={handleUpdateLine}
                >
                  Update
                </button>
              </div>
                </div>
                </div>
             
            </div>
          )}

          <div className="table-responsive mt-3">
            <table className="table m-0 forecast-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Model</th>
                  <th>Quoted Qty</th>
                  <th>Forecasted Value</th>
                  <th>Forecast Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forecastList.map((line, index) => (
                  <tr key={line?.id || line?.sId || index}>
                    <td>
                      {line?.productGroupName || currentForecast?.productGroupName || "-"}
                    </td>
                    <td>
                      {line?.productName || currentForecast?.productName || "-"}
                    </td>
                    <td className="text-right">
                      {line?.expectedQuantity ?? quotedQuantity ?? "-"}
                    </td>
                    <td className="text-right">
                      {FormatValueDecimal(
                        CurrencyConvertion(
                          line?.expectedAmount,
                          currentQuotation?.row?.conversionRate,
                          currencyDetail
                        )
                      )}
                    </td>
                    <td>
                      {line?.expectedDate
                        ? moment(new Date(line.expectedDate)).format("DD-MMM-YYYY")
                        : "-"}
                    </td>
                    <td>
                      <span>
                        <i
                          className="far fa-edit mr-2"
                          title="Edit"
                          onClick={() => handleEditClick(line)}
                        />
                      </span>
                      <span>
                        <i
                          className="fa fa-history mr-2"
                          title="History"
                          onClick={() => {
                            setOpenHistory({
                              show: true,
                              currentItem: line || null,
                            });
                          }}
                          />
                        </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end">
            <div className="text-align">
              <button
                type="button"
                className="btn cancel-btn mr-3"
                onClick={onHide}
              >
                Cancel
              </button>
            </div>
            <div className="text-align">
              <button
                type="button"
                className="btn save-btn"
                onClick={handleSaveForecast}
                disabled={!changeForecast && forecastList.length > 0}
              >
                Save
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        size="sm"
        show={openHistory?.show}
        onHide={handleCloseHistory}
        className="historyRightSideModalPopup drawer right-align"
      >
        <Modal.Header>
          <div className="d-flex align-items-center">
            <i className="fas fa-chevron-left" onClick={handleCloseHistory}></i>
            <h5 className="modal-title ml-2">Forecast History</h5>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="row forecast-history-table-row mt-3">
            <div className="col-12">
              <div className="table-responsive">
                <table className="table table-borderless m-0">
                  <thead>
                      <tr>
                      <th>Revision</th>
                      <th>Forecast Date</th>
                      <th>Revised Date</th>
                      <th>Revised On</th>
                      <th>Revised By</th>
                      </tr>
                  </thead>
                  <tbody>
                    {buildHistoryTimelineRows(openHistory?.currentItem?.histories || []).map((historyItem, historyIndex) => { 
                       const filterUser = userData && userData?.find(
                          (userItem) =>
                            userItem?.identityId === historyItem?.createdBy
                        );
                      return(
                      <tr key={historyIndex}>
                        <td className="text-nowrap">
                          {historyItem?.revisionLabel || `Rev ${String(historyIndex + 1).padStart(2, "0")}`}
                        </td>
                        <td className="text-nowrap">
                          {formatHistoryDate(historyItem?.previousDate)}
                        </td>
                        <td className="text-nowrap">
                          {formatHistoryDate(historyItem?.revisedDate || historyItem?.forecastedDate)}
                        </td>
                        <td className="text-nowrap">
                          {formatHistoryDate(historyItem?.revisedOn || historyItem?.createdOn, true)}
                        </td>
                        <td className="text-nowrap">
                              {(filterUser?.firstName || "") +
                                " " +
                                (filterUser?.lastName || "")}
                            </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <SaveSuccessModel
        createMsg={createMsg}
        setCreateMsg={setCreateMsg}
        setShow={setShowSuccess}
        show={showSuccess}
        SuccessDoneClick={SuccessDoneClick}
      />
      <ErrorMessage
        Show={errorShow?.show}
        errorMessage={errorShow?.errorMessage}
        ErrorDoneClick={ErrorDoneClick}
      />
    </>
  );
};

export default ForecastViewModal;
